import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateBatch } from '../../utils/autoTranslate';

/**
 * Catch-all page translator.
 *
 * The static dictionary (src/i18n) is the accurate path and always wins — anything it has already
 * rendered arrives here as Urdu and is skipped. This exists for everything it does not yet cover:
 * headings, buttons, tab labels and one-off sentences that would otherwise stay English on an Urdu
 * screen. It walks rendered text nodes, sends the English ones to LibreTranslate through our own
 * /translate proxy, and swaps them in place.
 *
 * WHAT IT REFUSES TO TOUCH, and why this matters on a financial system:
 *
 * - Anything without Latin letters — already translated, or a number.
 * - Account codes (INV-0013), CNIC, phone numbers, amounts, dates. Translating an identifier or a
 *   figure corrupts the record being displayed; these are matched and skipped explicitly.
 * - Form controls and code blocks. Rewriting an <input> value would change what gets submitted.
 * - Anything inside an element marked `data-no-translate` — used for names, which must never be
 *   rendered into another script.
 *
 * Originals are kept per text node, so switching back to English restores exactly rather than
 * round-tripping the text through the translator a second time.
 */

/** Nodes whose text is never prose. */
const SKIP_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'CODE', 'PRE', 'SCRIPT', 'STYLE']);

/**
 * Values that look like data rather than language. Kept deliberately broad: a false skip leaves a
 * word in English, whereas a false translate can corrupt an account code or an amount on screen.
 */
const DATA_LIKE = [
  /^[\s\d.,:/+()-]*$/, // numbers, amounts, dates, phone numbers
  /^[A-Z]{2,5}-\d+/, // INV-0013, DLR-0002, WD-R-000006
  /^PKR\b/i,
  /^\d{5}-?\d{7}-?\d$/, // CNIC
  /^[\w.+-]+@[\w-]+\.[\w.]+$/, // email
  /^https?:\/\//,
];

const originals = new WeakMap<Text, string>();

function isTranslatable(node: Text): boolean {
  const text = node.nodeValue?.trim() ?? '';
  if (text.length < 2 || text.length > 300) return false;

  // No Latin letters means it is already Urdu, or it is punctuation/digits.
  if (!/[A-Za-z]{2}/.test(text)) return false;
  if (DATA_LIKE.some((re) => re.test(text))) return false;

  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return false;
    if (el.hasAttribute('data-no-translate')) return false;
    if (el.isContentEditable) return false;
    el = el.parentElement;
  }
  return true;
}

function collect(root: Node): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const found: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    const node = current as Text;
    if (isTranslatable(node)) found.push(node);
    current = walker.nextNode();
  }
  return found;
}

async function translateTree(root: Node) {
  const nodes = collect(root);
  if (nodes.length === 0) return;

  // One request per sweep, deduplicated — a table of 50 rows shares most of its labels.
  const unique = [...new Set(nodes.map((n) => n.nodeValue!.trim()))];
  const map = await translateBatch(unique, 'ur');

  for (const node of nodes) {
    const source = node.nodeValue!.trim();
    const translated = map[source];
    if (!translated || translated === source) continue;

    if (!originals.has(node)) originals.set(node, node.nodeValue!);
    // Preserve the original leading/trailing whitespace so spacing between inline nodes survives.
    node.nodeValue = node.nodeValue!.replace(source, translated);
  }
}

function restore(root: Node) {
  for (const node of collect(root)) {
    const original = originals.get(node);
    if (original) node.nodeValue = original;
  }
}

export default function PageTranslator() {
  const { i18n } = useTranslation();
  const isUrdu = i18n.language.startsWith('ur');

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    if (!isUrdu) {
      restore(root);
      return;
    }

    let frame: number | undefined;
    /** Coalesces the burst of mutations a React render produces into one sweep. */
    const schedule = () => {
      window.clearTimeout(frame);
      frame = window.setTimeout(() => void translateTree(root), 120);
    };

    schedule();

    const observer = new MutationObserver((records) => {
      const meaningful = records.some(
        (r) => r.type === 'childList' ? r.addedNodes.length > 0 : r.type === 'characterData',
      );
      if (meaningful) schedule();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });

    return () => {
      observer.disconnect();
      window.clearTimeout(frame);
    };
  }, [isUrdu]);

  return null;
}
